import { useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import PageHero from "@/components/tex/PageHero";
import { staggerContainer, staggerItem } from "@/components/tex/PageTransition";

export default function AboutPage() {
  const { company } = useOutletContext();

  useEffect(() => {
    document.title = "About — Texcheme";
    window.scrollTo(0, 0);
  }, []);

  const brandName = company?.short_name || "Texcheme";
  const aboutBody = company?.about_body || [];
  const stats = company?.stats || [];
  const values = company?.values || [];

  return (
    <>
      <PageHero
        eyebrow="About us"
        title={`Bulk chemicals, delivered without drama.`}
        lead={company?.about_lead}
      />

      <section className="panel" data-testid="about-story">
        <div className="wrap about-grid">
          <div className="about-story">
            <div className="panel-head" style={{ marginBottom: 20 }}>
              <div className="eyebrow">Our story</div>
              <h2>How {brandName} works</h2>
            </div>
            {aboutBody.map((para, i) => (
              <p key={i} className="about-para">{para}</p>
            ))}
            {company?.mission && (
              <div className="mission-box">
                <span className="card-label">Our mission</span>
                <p>{company.mission}</p>
              </div>
            )}
          </div>

          <aside className="about-side">
            <motion.div
              className="stat-grid"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {stats.map((s, i) => (
                <motion.div className="stat-card" key={i} variants={staggerItem} data-testid={`stat-card-${i}`}>
                  <div className="stat-value">{s.value}</div>
                  <div className="stat-label">{s.label}</div>
                </motion.div>
              ))}
            </motion.div>
            <div className="card side-cert">
              <span className="card-label">Credentials</span>
              <p>{company?.certifications_note}</p>
              <p className="note">Founded {company?.founded_year} · {company?.hq_city}</p>
            </div>
          </aside>
        </div>
      </section>

      {values.length > 0 && (
        <section className="panel dark" data-testid="about-values">
          <div className="wrap">
            <div className="panel-head">
              <div className="eyebrow">What we optimize for</div>
              <h2>Three things we don&apos;t compromise on</h2>
            </div>
            <motion.div
              className="value-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.05 }}
            >
              {values.map((v, i) => (
                <motion.div className="value-card" key={i} variants={staggerItem} data-testid={`value-card-${i}`}>
                  <div className="value-num">{String(i + 1).padStart(2, "0")}</div>
                  <h3>{v.title}</h3>
                  <p>{v.body}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      <section className="panel cta-panel" data-testid="about-cta">
        <div className="wrap cta-panel-inner">
          <div>
            <h2>Want to see where we deliver?</h2>
            <p>Check our regions and depots, or get in touch about your plant.</p>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Link to="/coverage" className="btn btn-ghost">Coverage</Link>
            <Link to="/contact" className="btn btn-brand">Contact us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
